import React, {useState} from 'react';
import {Dropdown, DropdownToggle, DropdownMenu, DropdownItem} from 'reactstrap';
import Icon from '../shared/Icon.tsx';
import {PlusIcon} from 'lucide-react';
import {CaretDownIcon, SinkIcon} from '../shared/customIcons.ts';
import {SceneEntity} from './types.ts';
import AddModelButton from './AddModelButton.tsx';
import useClientConfig from '../../hooks/useClientConfig.tsx';

interface AddObjectMenuProps {
    addEntity: (entity: Omit<SceneEntity, 'elementId'>) => void;
}

const AddObjectMenu: React.FC<AddObjectMenuProps> = ({addEntity}) => {
    const [isOpen, setIsOpen] = useState(false);
    const {storageUri} = useClientConfig();

    const addModel = (url: string) => {
        addEntity({
            type: 'model',
            url,
            position: {x: 0, y: 0, z: 0},
            rotation: {x: 0, y: 0, z: 0},
            scaling: {x: 1, y: 1, z: 1}
        } as Omit<Extract<SceneEntity, {type: 'model'}>, 'elementId'>);
    };

    const addPointLight = () => {
        addEntity({
            type: 'pointLight',
            position: {x: 0, y: 0, z: 0},
            intensity: 1
        } as Omit<Extract<SceneEntity, {type: 'pointLight'}>, 'elementId'>);
    };

    const addDirectionalLight = () => {
        addEntity({
            type: 'directionalLight',
            position: {x: 0, y: 0, z: 0},
            direction: {x: 0, y: -1, z: 0},
            intensity: 0.5
        } as Omit<
            Extract<SceneEntity, {type: 'directionalLight'}>,
            'elementId'
        >);
    };

    const addSpotLight = () => {
        addEntity({
            type: 'spotLight',
            position: {x: 0, y: 0, z: 0},
            direction: {x: 0, y: -1, z: 0},
            angle: Math.PI / 4,
            exponent: 1,
            intensity: 0.8
        } as Omit<Extract<SceneEntity, {type: 'spotLight'}>, 'elementId'>);
    };

    const addHemisphericLight = () => {
        addEntity({
            type: 'hemisphericLight',
            direction: {x: 0, y: 1, z: 0},
            intensity: 0.5
        } as Omit<
            Extract<SceneEntity, {type: 'hemisphericLight'}>,
            'elementId'
        >);
    };

    const addTorusKnot = () => {
        addEntity({
            type: 'torusKnot',
            position: {x: 0, y: 0, z: 0},
            rotation: {x: 0, y: 0, z: 0},
            scaling: {x: 1, y: 1, z: 1},
            radius: 1,
            tube: 0.4,
            radialSegments: 64,
            tubularSegments: 64,
            p: 2,
            q: 3
        } as Omit<Extract<SceneEntity, {type: 'torusKnot'}>, 'elementId'>);
    };

    const addSphere = () => {
        addEntity({
            type: 'sphere',
            position: {x: 0, y: 0, z: 0},
            rotation: {x: 0, y: 0, z: 0},
            scaling: {x: 1, y: 1, z: 1},
            diameter: 2,
            material: {color: {r: 1, g: 0, b: 0}}
        } as Omit<Extract<SceneEntity, {type: 'sphere'}>, 'elementId'>);
    };

    const addBox = () => {
        addEntity({
            type: 'box',
            position: {x: 0, y: 0, z: 0},
            rotation: {x: 0, y: 0, z: 0},
            scaling: {x: 1, y: 1, z: 1},
            size: 1,
            material: {color: {r: 0, g: 1, b: 0}}
        } as Omit<Extract<SceneEntity, {type: 'box'}>, 'elementId'>);
    };

    const addTorus = () => {
        addEntity({
            type: 'torus',
            position: {x: 0, y: 0, z: 0},
            rotation: {x: 0, y: 0, z: 0},
            scaling: {x: 1, y: 1, z: 1},
            diameter: 1,
            thickness: 0.4,
            material: {color: {r: 0, g: 0, b: 1}}
        } as Omit<Extract<SceneEntity, {type: 'torus'}>, 'elementId'>);
    };

    const addCylinder = () => {
        addEntity({
            type: 'cylinder',
            position: {x: 0, y: 0, z: 0},
            rotation: {x: 0, y: 0, z: 0},
            scaling: {x: 1, y: 1, z: 1},
            height: 2,
            diameter: 1,
            material: {color: {r: 1, g: 1, b: 0}}
        } as Omit<Extract<SceneEntity, {type: 'cylinder'}>, 'elementId'>);
    };

    const addPlane = () => {
        addEntity({
            type: 'plane',
            position: {x: 0, y: 0, z: 0},
            rotation: {x: 0, y: 0, z: 0},
            scaling: {x: 1, y: 1, z: 1},
            width: 10,
            height: 10,
            material: {color: {r: 0.8, g: 0.8, b: 0.8}}
        } as Omit<Extract<SceneEntity, {type: 'plane'}>, 'elementId'>);
    };

    const addDisc = () => {
        addEntity({
            type: 'disc',
            position: {x: 0, y: 0, z: 0},
            rotation: {x: 0, y: 0, z: 0},
            scaling: {x: 1, y: 1, z: 1},
            radius: 1,
            material: {color: {r: 0, g: 1, b: 1}}
        } as Omit<Extract<SceneEntity, {type: 'disc'}>, 'elementId'>);
    };

    const addIcosphere = () => {
        addEntity({
            type: 'icosphere',
            position: {x: 0, y: 0, z: 0},
            rotation: {x: 0, y: 0, z: 0},
            scaling: {x: 1, y: 1, z: 1},
            radius: 1,
            subdivisions: 2,
            material: {color: {r: 1, g: 0, b: 1}}
        } as Omit<Extract<SceneEntity, {type: 'icosphere'}>, 'elementId'>);
    };

    const addLines = () => {
        addEntity({
            type: 'lines',
            position: {x: 0, y: 0, z: 0},
            rotation: {x: 0, y: 0, z: 0},
            scaling: {x: 1, y: 1, z: 1},
            points: [
                {x: 0, y: 0, z: 0},
                {x: 1, y: 1, z: 1},
                {x: 2, y: 0, z: 0}
            ],
            colors: [
                {r: 1, g: 0, b: 0, a: 1},
                {r: 0, g: 1, b: 0, a: 1},
                {r: 0, g: 0, b: 1, a: 1}
            ]
        } as Omit<Extract<SceneEntity, {type: 'lines'}>, 'elementId'>);
    };

    const addDashedLines = () => {
        addEntity({
            type: 'dashedLines',
            position: {x: 0, y: 0, z: 0},
            rotation: {x: 0, y: 0, z: 0},
            scaling: {x: 1, y: 1, z: 1},
            points: [
                {x: 0, y: 0, z: 0},
                {x: 1, y: 1, z: 1},
                {x: 2, y: 0, z: 0}
            ],
            dashSize: 0.2,
            gapSize: 0.1,
            dashNb: 10,
            colors: [
                {r: 1, g: 1, b: 0, a: 1},
                {r: 0, g: 1, b: 1, a: 1},
                {r: 1, g: 0, b: 1, a: 1}
            ]
        } as Omit<Extract<SceneEntity, {type: 'dashedLines'}>, 'elementId'>);
    };

    const addTube = () => {
        addEntity({
            type: 'tube',
            position: {x: 0, y: 0, z: 0},
            rotation: {x: 0, y: 0, z: 0},
            scaling: {x: 1, y: 1, z: 1},
            path: [
                {x: 0, y: 0, z: 0},
                {x: 1, y: 1, z: 0},
                {x: 2, y: 0, z: 0}
            ],
            radius: 0.1,
            tessellation: 16,
            cap: 3,
            material: {color: {r: 0, g: 0.6, b: 1}}
        } as Omit<Extract<SceneEntity, {type: 'tube'}>, 'elementId'>);
    };

    // No rotation: older runtimes read an extrudeShape's `rotation` as its
    // twist.
    const addExtrudeShape = () => {
        addEntity({
            type: 'extrudeShape',
            position: {x: 0, y: 0, z: 0},
            scaling: {x: 1, y: 1, z: 1},
            shape: [
                {x: 0, y: 0, z: 0},
                {x: 1, y: 0, z: 0},
                {x: 1, y: 1, z: 0},
                {x: 0, y: 1, z: 0}
            ],
            path: [
                {x: 0, y: 0, z: 0},
                {x: 0, y: 0, z: 1},
                {x: 0, y: 0, z: 2}
            ],
            scale: 0.5,
            cap: 3,
            material: {color: {r: 1, g: 0.5, b: 0}}
        } as Omit<Extract<SceneEntity, {type: 'extrudeShape'}>, 'elementId'>);
    };

    const addLathe = () => {
        addEntity({
            type: 'lathe',
            position: {x: 0, y: 0, z: 0},
            rotation: {x: 0, y: 0, z: 0},
            scaling: {x: 1, y: 1, z: 1},
            shape: [
                {x: 0, y: 0, z: 0},
                {x: 0.5, y: 0.5, z: 0},
                {x: 0, y: 1, z: 0},
                {x: 0, y: 1.5, z: 0}
            ],
            segments: 32,
            cap: 3,
            material: {color: {r: 0.5, g: 0, b: 1}}
        } as Omit<Extract<SceneEntity, {type: 'lathe'}>, 'elementId'>);
    };

    const addGround = () => {
        addEntity({
            type: 'ground',
            position: {x: 0, y: 0, z: 0},
            rotation: {x: 0, y: 0, z: 0},
            scaling: {x: 1, y: 1, z: 1},
            width: 20,
            height: 20,
            subdivisions: 100,
            material: {color: {r: 0.5, g: 0.5, b: 0.5}}
        } as Omit<Extract<SceneEntity, {type: 'ground'}>, 'elementId'>);
    };

    const addText = () => {
        addEntity({
            type: 'text',
            text: 'Hello',
            color: '#ffffff',
            height: 0.5,
            billboard: true,
            position: {x: 0, y: 1, z: 0}
        } as Omit<Extract<SceneEntity, {type: 'text'}>, 'elementId'>);
    };

    const addGroup = () => {
        addEntity({
            type: 'group',
            name: 'Group',
            position: {x: 0, y: 0, z: 0},
            rotation: {x: 0, y: 0, z: 0},
            scaling: {x: 1, y: 1, z: 1}
        } as Omit<Extract<SceneEntity, {type: 'group'}>, 'elementId'>);
    };

    const addKitchenSink = () => {
        const objects = [
            {
                type: 'sphere',
                position: {x: 0, y: 1, z: 0},
                rotation: {x: 0, y: 0, z: 0},
                scaling: {x: 1, y: 1, z: 1},
                diameter: 2,
                material: {color: {r: 1, g: 0, b: 0}}
            },
            {
                type: 'box',
                position: {x: 2, y: 1, z: 0},
                rotation: {x: 0, y: 0, z: 0},
                scaling: {x: 1, y: 1, z: 1},
                size: 1,
                material: {color: {r: 0, g: 1, b: 0}}
            },
            {
                type: 'torus',
                position: {x: -2, y: 1, z: 0},
                rotation: {x: 0, y: 0, z: 0},
                scaling: {x: 1, y: 1, z: 1},
                diameter: 1,
                thickness: 0.4,
                material: {color: {r: 0, g: 0, b: 1}}
            },
            {
                type: 'cylinder',
                position: {x: 4, y: 1, z: 0},
                rotation: {x: 0, y: 0, z: 0},
                scaling: {x: 1, y: 1, z: 1},
                height: 2,
                diameter: 1,
                material: {color: {r: 1, g: 1, b: 0}}
            },
            {
                type: 'plane',
                position: {x: 0, y: 0, z: 0},
                rotation: {x: Math.PI / 2, y: 0, z: 0},
                scaling: {x: 1, y: 1, z: 1},
                width: 10,
                height: 10,
                material: {color: {r: 0.8, g: 0.8, b: 0.8}}
            },
            {
                type: 'disc',
                position: {x: -4, y: 1, z: 0},
                rotation: {x: 0, y: 0, z: 0},
                scaling: {x: 1, y: 1, z: 1},
                radius: 1,
                material: {color: {r: 0, g: 1, b: 1}}
            },
            {
                type: 'icosphere',
                position: {x: 6, y: 1, z: 0},
                rotation: {x: 0, y: 0, z: 0},
                scaling: {x: 1, y: 1, z: 1},
                radius: 1,
                subdivisions: 2,
                material: {color: {r: 1, g: 0, b: 1}}
            },
            {
                type: 'model',
                url: `${storageUri}usefulstuff/51c392cb-1176-463a-a6f9-08d6bd51ab08/Duck.gltf`,
                position: {x: 0, y: 0, z: -2},
                rotation: {x: 0, y: 0, z: 0},
                scaling: {x: 1, y: 1, z: 1}
            },
            {
                type: 'pointLight',
                position: {x: 0, y: 5, z: 0},
                intensity: 1
            }
        ];

        objects.forEach(object =>
            addEntity(object as Omit<SceneEntity, 'elementId'>)
        );
    };

    const shapes: {label: string; onClick: () => void}[] = [
        {label: 'Sphere', onClick: addSphere},
        {label: 'Box', onClick: addBox},
        {label: 'Torus', onClick: addTorus},
        {label: 'Cylinder', onClick: addCylinder},
        {label: 'Plane', onClick: addPlane},
        {label: 'Disc', onClick: addDisc},
        {label: 'Icosphere', onClick: addIcosphere},
        {label: 'Torus Knot', onClick: addTorusKnot},
        {label: 'Ground', onClick: addGround},
        {label: 'Lines', onClick: addLines},
        {label: 'Dashed Lines', onClick: addDashedLines},
        {label: 'Tube', onClick: addTube},
        {label: 'Extrude Shape', onClick: addExtrudeShape},
        {label: 'Lathe', onClick: addLathe}
    ];

    const lights: {label: string; onClick: () => void}[] = [
        {label: 'Point Light', onClick: addPointLight},
        {label: 'Directional Light', onClick: addDirectionalLight},
        {label: 'Spot Light', onClick: addSpotLight},
        {label: 'Hemispheric Light', onClick: addHemisphericLight}
    ];

    const others: {label: string; onClick: () => void}[] = [
        {label: 'Text', onClick: addText},
        {label: 'Group', onClick: addGroup}
    ];

    return (
        <div className="d-flex flex-wrap gap-1 align-items-center">
            <Dropdown isOpen={isOpen} toggle={() => setIsOpen(o => !o)}>
                <DropdownToggle color="primary" size="sm">
                    <Icon icon={PlusIcon} /> Add object{' '}
                    <Icon icon={CaretDownIcon} />
                </DropdownToggle>
                <DropdownMenu container="body" className="scene-add-menu">
                    <DropdownItem header>Shapes</DropdownItem>
                    {shapes.map(item => (
                        <DropdownItem key={item.label} onClick={item.onClick}>
                            {item.label}
                        </DropdownItem>
                    ))}
                    <DropdownItem divider />
                    <DropdownItem header>Lights</DropdownItem>
                    {lights.map(item => (
                        <DropdownItem key={item.label} onClick={item.onClick}>
                            {item.label}
                        </DropdownItem>
                    ))}
                    <DropdownItem divider />
                    <DropdownItem header>Text and groups</DropdownItem>
                    {others.map(item => (
                        <DropdownItem key={item.label} onClick={item.onClick}>
                            {item.label}
                        </DropdownItem>
                    ))}
                    <DropdownItem divider />
                    <DropdownItem onClick={addKitchenSink}>
                        <Icon icon={SinkIcon} /> Kitchen Sink
                    </DropdownItem>
                </DropdownMenu>
            </Dropdown>

            <AddModelButton onModelSelected={addModel} />
        </div>
    );
};

export default AddObjectMenu;
